import { peerMetadataSchema, type PeerMetadata } from '@playcastdotio/Messaging';

export interface AdminPeerSessionIdentity { sessionId: string; peerId: string }

/** Binds the server-approved session UUID to its one current admin data-channel peer. */
export class AdminPeerSessionManager {
  private current: AdminPeerSessionIdentity | undefined;
  private readonly listeners = new Set<(sessionId: string) => void>();

  public connect(peerId: string, metadata: PeerMetadata): AdminPeerSessionIdentity | undefined {
    const parsed = peerMetadataSchema.safeParse(metadata);
    if (this.current && (this.current.peerId !== peerId ||
      !parsed.success || this.current.sessionId !== parsed.data.adminSessionId)) this.release(this.current.peerId);
    this.current = peerId && parsed.success && parsed.data.adminSessionId
      ? { peerId, sessionId: parsed.data.adminSessionId } : undefined;
    return this.current ? { ...this.current } : undefined;
  }

  public owns(sessionId: string, peerId: string | null): boolean {
    return !!this.current && this.current.sessionId === sessionId && this.current.peerId === peerId;
  }

  public matchesExit(identity: unknown, peerId: string): boolean {
    return identity === peerId || this.owns(String(identity), peerId);
  }

  public release(peerId: string): string | undefined {
    if (this.current?.peerId !== peerId) return undefined;
    const sessionId = this.current.sessionId;
    this.current = undefined;
    for (const listener of this.listeners) listener(sessionId);
    return sessionId;
  }

  public onReleased(listener: (sessionId: string) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
